import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Download, FileText, Calendar, Users, TrendingUp, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { csvSafe } from '../utils/csvSafe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reports = () => {
  const { token, user: authUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamLeads, setTeamLeads] = useState([]);
  const [teamLeadFilter, setTeamLeadFilter] = useState([]);
  const [teamPerf, setTeamPerf] = useState(null);
  const [teamPerfLoading, setTeamPerfLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTeamPerformance();
  }, [teamLeadFilter, token]);

  const loadData = async () => {
    try {
      const [overviewRes, tasksRes, usersRes, leadsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/team-leads`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOverview(overviewRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setTeamLeads(leadsRes.data || []);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamPerformance = async () => {
    if (!token) return;
    setTeamPerfLoading(true);
    try {
      const params = {};
      if (teamLeadFilter.length > 0) params.team_lead_ids = teamLeadFilter.join(',');
      const r = await axios.get(`${API}/analytics/team-performance`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setTeamPerf(r.data);
    } catch (e) {
      // Non-fatal
    } finally {
      setTeamPerfLoading(false);
    }
  };

  const exportTeamPerformanceCSV = () => {
    if (!teamPerf) return;
    const rows = teamPerf.workload_distribution.map(m => [
      m.full_name, m.role_level || '', m.status, m.total, m.completed, m.active, m.overdue, `${m.completion_rate}%`
    ]);
    const headers = ['Member', 'Role', 'Status', 'Total', 'Completed', 'Active', 'Overdue', 'Completion Rate'];
    const csv = [headers, ...rows].map(r => r.map(v => `"${csvSafe(v)}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-performance-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('Team Performance exported!');
  };

  const exportTasksCSV = () => {
    const userMap = {};
    users.forEach(u => { userMap[u.id] = u.full_name || u.email; });
    const headers = ['Task ID', 'Task', 'Category', 'Status', 'Output', 'Assigned To', 'Start Date', 'End Date'];
    const rows = tasks.map(t => [
      t.task_id || '',
      t.task,
      t.category,
      t.your_status,
      t.output || '',
      t.task_done_by ? (userMap[t.task_done_by] || 'Unknown User') : '',
      t.start_date ? format(new Date(t.start_date), 'yyyy-MM-dd') : '',
      t.your_end_date ? format(new Date(t.your_end_date), 'yyyy-MM-dd') : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${csvSafe(cell)}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('Report exported!');
  };

  const generatePDFReport = () => {
    // In a real implementation, you would use a library like jsPDF
    toast.info('PDF generation coming soon!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Prepare chart data
  const statusData = [
    { name: 'Completed', value: overview?.completed || 0, fill: '#10b981' },
    { name: 'In Progress', value: overview?.in_progress || 0, fill: '#3b82f6' },
    { name: 'Yet to Start', value: overview?.yet_to_start || 0, fill: '#f59e0b' },
    { name: 'On Hold', value: overview?.on_hold || 0, fill: '#ef4444' }
  ];

  const userPerformanceData = Object.entries(overview?.users || {}).slice(0, 10).map(([userId, data]) => {
    const user = users.find(u => u.id === userId);
    return {
      name: user?.full_name || `User ${userId.slice(0, 8)}`,
      total: data.total,
      completed: data.completed
    };
  });

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-3">
            <FileText className="text-cyan-500" size={32} />
            Reports & Analytics
          </h1>
          <p className="page-subtitle">Export data and view performance metrics</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportTasksCSV} variant="outline" data-testid="export-csv-button">
            <Download size={18} className="mr-2" /> Export CSV
          </Button>
          <Button onClick={generatePDFReport} data-testid="export-pdf-button" className="btn-cta">
            <FileText size={18} className="mr-2" /> Generate PDF
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="metric-card border-l-4 border-blue-400">
          <p className="text-sm font-medium text-slate-600 mb-1">Total Tasks</p>
          <p className="text-3xl font-bold text-slate-900">{overview?.total_tasks || 0}</p>
        </div>
        <div className="metric-card border-l-4 border-green-400">
          <p className="text-sm font-medium text-slate-600 mb-1">Completion Rate</p>
          <p className="text-3xl font-bold text-slate-900">
            {overview?.total_tasks ? Math.round((overview.completed / overview.total_tasks) * 100) : 0}%
          </p>
        </div>
        <div className="metric-card border-l-4 border-amber-400">
          <p className="text-sm font-medium text-slate-600 mb-1">Active Tasks</p>
          <p className="text-3xl font-bold text-slate-900">{overview?.in_progress || 0}</p>
        </div>
        <div className="metric-card border-l-4 border-red-400">
          <p className="text-sm font-medium text-slate-600 mb-1">Blocked Tasks</p>
          <p className="text-3xl font-bold text-slate-900">{overview?.dev_blocked || 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="chart-container">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Task Status Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* User Performance */}
        <div className="chart-container">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Team Performance</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userPerformanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="total" fill="#06b6d4" name="Total Tasks" radius={[8, 8, 0, 0]} />
              <Bar dataKey="completed" fill="#10b981" name="Completed" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="chart-container">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Category Distribution</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(overview?.categories || {}).map(([category, count]) => (
            <div key={category} className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{count}</p>
              <p className="text-sm text-slate-600 mt-1">{category}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Content Type Distribution */}
      <div className="chart-container" data-testid="content-type-distribution">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">Content Type Distribution</h2>
          <span className="text-xs text-slate-500">
            {Object.keys(overview?.content_types || {}).length} types tracked
          </span>
        </div>
        {Object.keys(overview?.content_types || {}).length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            No tasks have a content type set yet. Add a content type when creating or editing tasks to see analytics here.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(overview?.content_types || {})
              .sort((a, b) => (b[1].total || 0) - (a[1].total || 0))
              .map(([ct, stats]) => {
                const rate = stats.completion_rate || 0;
                const rateColor =
                  rate >= 70 ? 'bg-emerald-500' : rate >= 40 ? 'bg-amber-500' : 'bg-rose-500';
                const textColor =
                  rate >= 70 ? 'text-emerald-700' : rate >= 40 ? 'text-amber-700' : 'text-rose-700';
                return (
                  <div
                    key={ct}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:shadow-sm transition-shadow"
                    data-testid={`content-type-card-${ct}`}
                  >
                    <div className="flex items-baseline justify-between mb-2">
                      <p className="text-sm font-semibold text-slate-700 truncate">{ct}</p>
                      <p className={`text-xs font-bold ${textColor}`}>{rate}%</p>
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mb-2">{stats.total}</p>
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
                      <div
                        className={`h-full ${rateColor} transition-all`}
                        style={{ width: `${rate}%` }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-600">
                      <span>✅ Done: {stats.completed}</span>
                      <span>⏳ Active: {stats.in_progress}</span>
                      <span>○ Pending: {stats.yet_to_start}</span>
                      <span>⏸ On Hold: {stats.on_hold}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* ==================== TEAM PERFORMANCE REPORT ==================== */}
      <div
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        data-testid="team-performance-report"
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users size={24} className="text-cyan-500" />
            Team Performance
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            {teamLeads.length > 0 && (
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600 whitespace-nowrap">Team:</Label>
                <MultiSelectFilter
                  label="Team"
                  placeholder="All Teams"
                  triggerClassName="w-64"
                  options={teamLeads.map(l => ({ value: l.id, label: `${l.full_name}'s Team` }))}
                  selectedValues={teamLeadFilter}
                  onChange={setTeamLeadFilter}
                  testId="team-perf-filter"
                />
              </div>
            )}
            <Button
              onClick={exportTeamPerformanceCSV}
              variant="outline"
              size="sm"
              data-testid="export-team-perf-csv"
              disabled={!teamPerf}
            >
              <Download size={16} className="mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {teamPerfLoading ? (
          <div className="text-center py-10 text-slate-500">Loading team performance…</div>
        ) : teamPerf ? (
          <>
            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
              {[
                { label: 'Total Tasks', value: teamPerf.summary.total, tone: 'text-slate-800' },
                { label: 'Completed', value: teamPerf.summary.completed, tone: 'text-green-600' },
                { label: 'In Progress', value: teamPerf.summary.in_progress, tone: 'text-blue-600' },
                { label: 'Pending Approval', value: teamPerf.summary.pending_approval, tone: 'text-purple-600' },
                { label: 'On Hold', value: teamPerf.summary.on_hold, tone: 'text-amber-600' },
                { label: 'Backlog', value: teamPerf.summary.backlog, tone: 'text-slate-600' },
                { label: 'Completion Rate', value: `${teamPerf.summary.completion_rate}%`, tone: 'text-cyan-700' },
                { label: 'Overdue', value: teamPerf.summary.overdue, tone: 'text-red-600' },
                { label: 'Avg Time (h)', value: teamPerf.summary.avg_completion_hours, tone: 'text-slate-800' },
              ].map(k => (
                <div key={k.label} className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid={`team-perf-kpi-${k.label}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{k.label}</p>
                  <p className={`text-lg font-bold mt-1 ${k.tone}`}>{k.value}</p>
                </div>
              ))}
            </div>

            {/* Productivity Trend chart */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <TrendingUp size={16} className="text-cyan-500" /> Productivity Trend (8 Weeks)
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={teamPerf.productivity_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                  <Tooltip />
                  <Bar dataKey="completed" fill="#06b6d4" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Workload distribution table */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Users size={16} className="text-cyan-500" /> Workload Distribution
              </h3>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Member</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Role</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Status</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Total</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Done</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Active</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Overdue</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {teamPerf.workload_distribution.map(m => (
                      <tr key={m.user_id} data-testid={`team-perf-row-${m.user_id}`}>
                        <td className="px-4 py-2 font-medium text-slate-800">{m.full_name}</td>
                        <td className="px-4 py-2 text-slate-600">{m.role_level || '—'}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {m.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">{m.total}</td>
                        <td className="px-4 py-2 text-right text-green-600 font-semibold">{m.completed}</td>
                        <td className="px-4 py-2 text-right text-blue-600">{m.active}</td>
                        <td className={`px-4 py-2 text-right ${m.overdue > 0 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>{m.overdue}</td>
                        <td className="px-4 py-2 text-right font-semibold text-cyan-700">{m.completion_rate}%</td>
                      </tr>
                    ))}
                    {teamPerf.workload_distribution.length === 0 && (
                      <tr><td colSpan={8} className="text-center py-6 text-slate-500 italic">No team members in scope</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-slate-500 italic">
            Team performance data is not available for your role.
          </div>
        )}
      </div>

      {/* Report Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar size={24} className="text-cyan-500" />
          Report Summary
        </h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Report Generated:</span>
            <span className="font-semibold text-slate-900">{format(new Date(), 'MMMM dd, yyyy HH:mm')}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Total Team Members:</span>
            <span className="font-semibold text-slate-900">{users.length}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Total Tasks Tracked:</span>
            <span className="font-semibold text-slate-900">{tasks.length}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Average Completion Rate:</span>
            <span className="font-semibold text-green-600">
              {overview?.total_tasks ? ((overview.completed / overview.total_tasks) * 100).toFixed(1) : 0}%
            </span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-600">Tasks Needing Attention:</span>
            <span className="font-semibold text-red-600">{(overview?.overdue || 0) + (overview?.dev_blocked || 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;